import { useEffect, useMemo, useState } from "react";

import {
  getMarketItemById,
  getMarketItems,
  getMarketSections,
  reserveMarketItem,
} from "../api/api";
import { Button } from "../components/Button";
import { getItemImages } from "./market/getItemImages";
import { MarketItemDetailPanel } from "./market/MarketItemDetailPanel";
import { MarketSectionsList } from "./market/MarketSectionsList";

export function Market({ onBack }) {
  const [sections, setSections] = useState([]);
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItemImage, setSelectedItemImage] = useState("");
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reservingItemId, setReservingItemId] = useState(null);
  const [success, setSuccess] = useState("");
  const [showFullImage, setShowFullImage] = useState(false);

  useEffect(() => {
    let mounted = true;
    Promise.all([getMarketSections(), getMarketItems()])
      .then(([sectionsData, itemsData]) => {
        if (!mounted) return;
        setSections(sectionsData);
        setItems(itemsData);
      })
      .catch((err) => {
        if (!mounted) return;
        setError(err?.message || "Не удалось загрузить магазин");
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
      });
    return () => (mounted = false);
  }, []);

  const groupedSections = useMemo(() => {
    const grouped = sections.map((section) => ({
      ...section,
      items: items.filter((item) => item.section_id === section.id),
    }));
    return grouped.filter((section) => section.items.length > 0);
  }, [sections, items]);

  const onOpenItem = async (itemId) => {
    if (loadingDetails) return;
    setLoadingDetails(true);
    setError("");
    try {
      const full = await getMarketItemById(itemId);
      setSelectedItem(full);
      setSelectedItemImage(getItemImages(full)[0] || "");
    } catch (err) {
      setError(err?.message || "Не удалось открыть карточку товара");
    } finally {
      setLoadingDetails(false);
    }
  };

  const onCloseItem = () => {
    setSelectedItem(null);
    setSelectedItemImage("");
    setShowFullImage(false);
  };

  const onReserve = async (itemId, paymentType) => {
    if (reservingItemId) return;
    setReservingItemId(itemId);
    setError("");
    setSuccess("");
    try {
      const result = await reserveMarketItem(itemId, paymentType);
      setSuccess(result?.message || "Товар забронирован на 24 часа");
      const [sectionsData, itemsData] = await Promise.all([getMarketSections(), getMarketItems()]);
      setSections(sectionsData);
      setItems(itemsData);
      if (selectedItem?.id === itemId) {
        const full = await getMarketItemById(itemId);
        setSelectedItem(full);
        setSelectedItemImage((prev) => prev || getItemImages(full)[0] || "");
      }
    } catch (err) {
      setError(err?.message || "Не удалось забронировать товар");
    } finally {
      setReservingItemId(null);
    }
  };

  return (
    <section className="client">
      <header className="sectionHeader">
        <h2>Маркет и мерч</h2>
        <p>Товары и мерч от Стихии.</p>
      </header>

      <div className="eventsToolbar">
        <Button onClick={onBack}>Назад</Button>
      </div>

      {loading ? <p className="muted">Загрузка...</p> : null}
      {!loading && error ? <p className="errorMessage">{error}</p> : null}
      {!loading && success ? <p className="successMessage">{success}</p> : null}

      {!loading && !error && selectedItem ? (
        <MarketItemDetailPanel
          selectedItem={selectedItem}
          selectedItemImage={selectedItemImage}
          setSelectedItemImage={setSelectedItemImage}
          showFullImage={showFullImage}
          setShowFullImage={setShowFullImage}
          onCloseItem={onCloseItem}
          onReserve={onReserve}
          reservingItemId={reservingItemId}
        />
      ) : null}

      {!loading && !error && !selectedItem ? (
        <MarketSectionsList
          groupedSections={groupedSections}
          loadingDetails={loadingDetails}
          reservingItemId={reservingItemId}
          onOpenItem={onOpenItem}
          onReserve={onReserve}
        />
      ) : null}
    </section>
  );
}
